// ═══════════════════════════════════════════════════════════════════════════
//  LIRIK KARAOKE — bahan untuk tampilan sapuan (wipe) di layar TV (5 Okt 2026)
//
//  Dua sumber, satu bentuk (BarisKaraoke):
//    · lagu upload  → GET /api/t/:kode/lagu/:id   (lirik .lrc + kata_json
//                     engine lokal bila ada → sapuan per kata)
//    · YouTube/Spotify → GET /api/t/:kode/lirik-luar?antrean=<id>  (LRCLIB,
//                     dicari & disinggahkan backend — handlers/lirik_luar.go)
//
//  Klien sendiri (tanpa token), sama alasannya dengan lib/tamu.ts: permintaan
//  dari TV tidak boleh menumpang token admin yang kebetulan tersimpan.
// ═══════════════════════════════════════════════════════════════════════════

import axios from 'axios'
import type { BerkasLagu } from './tamu'

const t = axios.create({ baseURL: '/api/t' })

/** Satu kata bertimestamp (detik). */
export type KataKaraoke = { t0: number; t1: number; teks: string }

/** Satu baris karaoke: mulai `t`, selesai `a` (detik), kata opsional. */
export type BarisKaraoke = { t: number; a: number; teks: string; kata?: KataKaraoke[] }

/** Format kata_json engine lokal (worker-mac/worker.py susun_baris). */
type BarisMesin = {
  start: number; end: number; text: string
  words?: { start: number; end: number; word: string }[]
}

export type BerkasBerkata = BerkasLagu & { kata?: BarisMesin[] | null }

const norm = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')

/**
 * Perkiraan selesai baris tanpa waktu per kata: sepanjang teksnya
 * (±0,17 dtk/huruf, min 1,8 dtk), tapi tidak melewati baris berikutnya.
 * Sama dengan uraiLRCLuar di backend.
 */
export function perkiraanAkhir(mulai: number, teks: string, berikut: number): number {
  const kira = Math.max(1.8, [...teks].length * 0.17 + 0.6)
  return Math.max(mulai + 0.4, Math.min(berikut - 0.15, mulai + kira))
}

/** Lagu upload → baris karaoke. Kosong bila lirik belum bersinkron. */
export function susunDariBerkas(b: BerkasBerkata | null): BarisKaraoke[] {
  if (!b || !b.tersinkron || !b.lirik?.length) return []
  const mesin = Array.isArray(b.kata) ? b.kata : []
  const out: BarisKaraoke[] = []
  let j = 0
  for (let i = 0; i < b.lirik.length; i++) {
    const l = b.lirik[i]
    const berikut = i + 1 < b.lirik.length ? b.lirik[i + 1].t : l.t + 8
    // Cocokkan dengan baris mesin: waktu mulai sama (LRC dibulatkan 2
    // desimal) dan teksnya sama. Tidak cocok → sapuan per baris.
    let cocok: BarisMesin | undefined
    while (j < mesin.length && mesin[j].start < l.t - 0.06) j++
    for (let k = j; k < mesin.length && mesin[k].start <= l.t + 0.06; k++) {
      if (norm(mesin[k].text) === norm(l.teks)) { cocok = mesin[k]; break }
    }
    const kata = (cocok?.words ?? [])
      .filter(w => (w.word ?? '').trim() && w.end >= w.start)
      .map(w => ({ t0: w.start, t1: w.end, teks: w.word.trim() }))
    if (cocok && kata.length) {
      out.push({ t: l.t, a: Math.max(l.t + 0.3, Math.min(berikut, cocok.end)), teks: l.teks, kata })
    } else {
      out.push({ t: l.t, a: perkiraanAkhir(l.t, l.teks, berikut), teks: l.teks })
    }
  }
  return out
}

// ── LEWATI INTRO CERITA (22 Okt 2026) ─────────────────────────────────────
//
// Zaldy: "bisa auto crop video jadi mulai beberapa detik sebelum lagu aja —
// kalau videonya ada ceritanya jadi panjang banget". Tidak mengubah berkas:
// pemutar MULAI dari detik ini (waktu lirik tetap absolut, jadi sinkron).
// Hanya bila baris bernyanyi pertama datang > AMBANG_INTRO detik — intro
// musik biasa (≤ 20 dtk) tidak disentuh; selebihnya dimulai JEDA_SEBELUM
// detik sebelum baris pertama supaya intro musiknya masih terdengar.
const AMBANG_INTRO = 20
const JEDA_SEBELUM = 8

export function mulaiLewatiIntro(baris: BarisKaraoke[]): number {
  const pertama = baris.find(b => b.teks.trim() && b.teks.trim() !== '♪' && b.t > 0)
  if (!pertama || pertama.t <= AMBANG_INTRO) return 0
  return Math.max(0, Math.floor(pertama.t - JEDA_SEBELUM))
}

// ── LIRIK LUAR (LRCLIB lewat backend) ─────────────────────────────────────

export type JawabLirikLuar = {
  ketemu: boolean
  baris: { t: number; a: number; teks: string; k?: [number, number, string][] }[]
  judul?: string; artis?: string; durasi?: number
}

// ── 21 Okt 2026: ROMANISASI ─────────────────────────────────────────────
// Zaldy: "lagu bahasa selain Indonesia/Inggris: atas huruf asal negara, bawah
// huruf abjad". Bacaan latin dihitung server (handlers/romanisasi.go: Hangul,
// Kanji/Kana, Hanzi, Kiril). Baris yang sudah latin tidak dikirim.
export const perluRomanisasi = (s: string) =>
  /[\p{Script=Han}\p{Script=Hangul}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Cyrillic}\p{Script=Greek}]/u.test(s)

export async function romanisasiLirik(kode: string, baris: string[]): Promise<Record<string, string>> {
  const unik = Array.from(new Set(baris.filter(perluRomanisasi)))
  if (!unik.length) return {}
  const r = await t.post<{ rom: string[] }>(`/${kode}/romanisasi`, { baris: unik }, { timeout: 20000 })
  const out: Record<string, string> = {}
  unik.forEach((s, i) => { const v = r.data?.rom?.[i]; if (v) out[s] = v })
  return out
}

export async function ambilLirikLuar(kode: string, antreanId: string): Promise<BarisKaraoke[]> {
  return (await ambilLirikLuarRinci(kode, antreanId)).baris
}

/** 21 Okt 2026: baris + durasi rekaman LRCLIB (detik, 0 bila tidak diketahui)
 *  — pemutar YouTube membandingkannya dengan durasi video untuk tahu lirik
 *  itu versi lain (intro/tempo beda) dan menyembunyikannya. */
export async function ambilLirikLuarRinci(kode: string, antreanId: string): Promise<{ baris: BarisKaraoke[]; durasi: number }> {
  // Backend membatasi pencarian ±9 dtk; 12 dtk di sini supaya jawabannya
  // sempat tiba. Gagal = tanpa lirik (pemanggil menelan galatnya).
  const r = await t.get<JawabLirikLuar>(`/${kode}/lirik-luar`, {
    params: { antrean: antreanId }, timeout: 15000,
  })
  if (!r.data?.ketemu || !Array.isArray(r.data.baris)) return { baris: [], durasi: 0 }
  const baris = r.data.baris
    .filter(b => typeof b.t === 'number' && b.teks)
    .map(b => ({
      t: b.t, a: b.a > b.t ? b.a : b.t + 2, teks: b.teks,
      kata: b.k?.length ? b.k.map(([t0, t1, teks]) => ({ t0, t1, teks })) : undefined,
    }))
  return { baris, durasi: typeof r.data.durasi === 'number' ? r.data.durasi : 0 }
}

// ── SETELAN LIRIK PER VIDEO (offset & sembunyi) ───────────────────────────
//
// Disimpan di TV per video/trek (bukan per baris antrean): lagu yang sama
// diputar lagi besok tetap memakai geseran yang sudah pas. Belum ada kolom
// antrean untuk ini — kalau remote nanti perlu mengaturnya, tambah kolom
// lirik_offset/lirik_tampil di kr_antrean dan baca di sini.

export type SetelanLirik = { offset: number; sembunyi: boolean }
// 27 Okt: v2 — tombol geser/sembunyi di TV dihapus; geseran lama yang tersimpan di TV
// (tak bisa lagi direset dari layar) dibuang. Geser resmi = dari tablet remote.
const KUNCI_SETEL = 'kr_lirik_setel_v2'

function bacaSemua(): Record<string, SetelanLirik> {
  try { return JSON.parse(localStorage.getItem(KUNCI_SETEL) || '{}') || {} } catch { return {} }
}

export function bacaSetelanLirik(kunci: string): SetelanLirik {
  const s = bacaSemua()[kunci]
  return {
    offset: typeof s?.offset === 'number' && isFinite(s.offset) ? s.offset : 0,
    sembunyi: !!s?.sembunyi,
  }
}

export function simpanSetelanLirik(kunci: string, s: SetelanLirik) {
  try {
    const semua = bacaSemua()
    if (s.offset === 0 && !s.sembunyi) delete semua[kunci]
    else semua[kunci] = s
    const kunciKunci = Object.keys(semua)
    // Batas 400 video — yang paling lama dibuang.
    for (let i = 0; i < kunciKunci.length - 400; i++) delete semua[kunciKunci[i]]
    localStorage.setItem(KUNCI_SETEL, JSON.stringify(semua))
  } catch { /* privat / penuh */ }
}

// ── JAM HALUS ─────────────────────────────────────────────────────────────
//
// player.getCurrentTime() YouTube diperbarui per beberapa ratus ms. Dibaca
// paling sering 10×/dtk, lalu diteruskan (ekstrapolasi) antar-bacaan supaya
// sapuan bergerak mulus 60 fps, bukan melompat-lompat.

export function buatJamHalus(baca: () => { t: number; main: boolean } | null): () => number {
  let dasarT = 0, dasarNow = 0, bacaTerakhir = -1e9, nilaiTerakhir = -1, main = false
  return () => {
    const now = performance.now()
    if (now - bacaTerakhir >= 100) {
      bacaTerakhir = now
      const r = baca()
      if (r && isFinite(r.t)) {
        const kira = dasarT + (main ? (now - dasarNow) / 1000 : 0)
        if (!r.main) {
          dasarT = r.t; dasarNow = now
        } else if (r.t !== nilaiTerakhir || !main) {
          // Lompatan besar (seek/mulai) → ikuti langsung; selisih kecil →
          // dihaluskan supaya sapuan tidak tersentak mundur.
          dasarT = !main || Math.abs(kira - r.t) > 0.6 ? r.t : kira + (r.t - kira) * 0.35
          dasarNow = now
        }
        nilaiTerakhir = r.t
        main = r.main
      }
    }
    return dasarT + (main ? (now - dasarNow) / 1000 : 0)
  }
}
